import React, { createRef, useEffect, useState } from 'react';
import { PhysicianFragment, usePhysiciansInfoLazyQuery, usePhysiciansInfoQuery } from '../graphql';
import { Button, Col, Form, Row, Modal } from 'react-bootstrap';
import { SearchInputWithGql, SearchInputWithGqlDisplay } from './SearchInputWithGql';
import { FIELD_NAMES, getConfigurationDataKey } from '../utils';
import { AnyObject } from 'final-form';
import { AddPrescriberView } from '../views';
import { useForm } from 'react-final-form';
import { AnyType } from '../interfaces';
import { ResourceContent } from '../components';

const { prescriberFields } = FIELD_NAMES;

export const AddSearchPrescriber = (props: AnyObject) => {
  const form = useForm();
  const ref = createRef<HTMLDivElement>();
  const [prescriberId, setPrescriberId] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const organizationId = props?.organizationId || '';
  const { data, loading, refetch } = usePhysiciansInfoQuery({ variables: { organizationId } });
  const [defaultPrescriber, setDefaultPrescriber] = useState<any>(null);
  const [prescriberAdded, setPriscriberAdded] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  useEffect(() => {
    if (organizationId && prescriberId) {
      refetch();
    }
  }, [prescriberId]);

  useEffect(() => {
    if (!prescriberAdded) {
      setDefaultPrescriber(props?.defaultPrescriber);
      setPrescriberId(props?.defaultPrescriber?.physicianId || props?.defaultPrescriber?.id);
    }
  }, [props?.defaultPrescriber, prescriberAdded]);

  useEffect(() => {
    if (prescriberId && data) {
      props.onPrescriberSelect(data?.result?.find((p: AnyType) => prescriberId?.toString() === p?.id));
    }

    if (prescriberAdded) {
      const prescriber = data?.result?.find((p: AnyType) => prescriberId?.toString() === p?.id);
      setDefaultPrescriber(prescriber);
    }
  }, [data, prescriberId]);

  const toggleAddInitialValue = !!(
    form.getState().values['adding_prescriber'] || form.getState().values[prescriberFields.id]
  );
  const [toggleAdd, setToggleAdd] = useState<boolean>(toggleAddInitialValue);

  const toggleReadFields = () => {
    if (form.getState().values['adding_prescriber'] === false) {
      form.change(prescriberFields.id, '');
      form.change(prescriberFields.npi, '');
      form.change(prescriberFields.firstName, '');
      form.change(prescriberFields.middleName, '');
      form.change(prescriberFields.lastName, '');
      form.change(prescriberFields.taxId, '');
      form.change(prescriberFields.stateLicenseNumber, '');
      form.change(prescriberFields.licensingState, '');
      form.change(prescriberFields.ptan, '');
      form.change(prescriberFields.dea, '');
      form.change(prescriberFields.email, '');
    }
  };

  const onPrescriberSelect = (prescriber: PhysicianFragment | null) => {
    form.batch(() => {
      form.change('adding_prescriber', false);

      setToggleAdd(false);
      form.change(prescriberFields.id, prescriber?.id);
      form.change(prescriberFields.firstName, prescriber?.firstName);
      form.change(prescriberFields.middleName, prescriber?.middleName);
      form.change(prescriberFields.lastName, prescriber?.lastName);
      form.change(prescriberFields.npi, prescriber?.npi || null);
      form.change(prescriberFields.taxId, prescriber?.details?.taxId);
      form.change(prescriberFields.stateLicenseNumber, prescriber?.stateLicenses?.stateLicenseNumber);
      form.change(prescriberFields.licensingState, prescriber?.stateLicenses?.stateIssuer);
      form.change(prescriberFields.ptan, prescriber?.details?.pTAN);
      form.change(prescriberFields.dea, prescriber?.details?.dEA);
      form.change(prescriberFields.email, prescriber?.email);
    });
    setPrescriberId(prescriber?.id);
    props.onPrescriberSelect(prescriber);
  };

  const addedNewPrescriber = (prescriberId: number | null | undefined) => {
    setPriscriberAdded(true);
    setPrescriberId(prescriberId);
    setIsModalVisible(false);
  };

  const getAddedPrescriber = () => {
    const prescriber: any =
      data?.result?.filter(
        (p: AnyType) => (prescriberId?.toString() || defaultPrescriber?.physicianId || defaultPrescriber?.id) == p?.id
      ) || [];
    if (!props?.prescriber) {
      props.setPrescriber(prescriber?.[0]);
    }
    return prescriber;
  };

  return (
    <>
      <Form.Group {...props.sizes}>
        <p className='nr-text mt-3 mb-2 text-uppercase'>SELECT THE PRESCRIBER</p>
        <div className='mb-2'> Search Registered Prescribers:</div>
        <Row>
          <Col className='mb-3'>
            <Row>
              <Col className='w-75 nr-patient'>
                {prescriberId &&
                  data?.result &&
                  !loading &&
                  data?.result.filter((p: AnyType) => prescriberId?.toString() === p?.id).length > 0 && (
                    <SearchInputWithGql
                      asyncTypeaheadProps={{
                        ...props.asyncTypeaheadProps,
                        ref,
                        defaultSelected: data?.result ? getAddedPrescriber() : []
                      }}
                      display={SearchInputWithGqlDisplay.outlined}
                      labelRenderer={(option: PhysicianFragment) =>
                        `${option.firstName} ${option.lastName} | ${option.npi}`
                      }
                      queryHook={usePhysiciansInfoLazyQuery}
                      onItemSelect={onPrescriberSelect}
                      placeholder='Search Prescriber'
                      isDisabled={props?.isDisabled}
                      npiSearchButtonIcon={props.npiInfoSearch === 'NPISearch' ? 'NPISearch' : ''}
                    />
                  )}
                {!prescriberId && (
                  <SearchInputWithGql
                    asyncTypeaheadProps={{
                      ref
                    }}
                    display={SearchInputWithGqlDisplay.outlined}
                    labelRenderer={(option: PhysicianFragment) =>
                      `${option.firstName} ${option.lastName} | ${option.npi}`
                    }
                    queryHook={usePhysiciansInfoLazyQuery}
                    onItemSelect={onPrescriberSelect}
                    placeholder='Prescriber Name or NPI'
                    npiSearchButtonIcon=''
                  />
                )}
              </Col>
              <Col>
                <Button
                  variant='outline-secondary-new'
                  className='ml-2 text-nowrap'
                  onClick={() => setIsModalVisible(true)}
                  disabled={props?.isDisabled}>
                  <i className={toggleAdd ? '' : 'fa-light fa-stethoscope'} style={{ marginRight: '8px' }}></i>
                  {toggleAdd ? 'CANCEL' : 'Add New Prescriber'}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form.Group>

      <Modal show={isModalVisible} onHide={() => setIsModalVisible(false)} centered size='lg'>
        <Modal.Header closeButton>
          <div className='d-flex w-100 align-items-center justify-content-between'>
            <Modal.Title as='h5'>Add New Prescriber</Modal.Title>
            <p className='required-text general-note mb-0 ms-3'>
              <ResourceContent contentKey={getConfigurationDataKey('general.required-fields-note')} />
            </p>
          </div>
        </Modal.Header>
        <Modal.Body>
          <AddPrescriberView
            isStartRequestPage={true}
            patientId={props.patientId}
            addedNewPrescriber={addedNewPrescriber}
            setShowModal={setIsModalVisible}
            setLoading={setIsSubmitting}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};
